import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

export function useCreateClinicCondition() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: { type: "allergy" | "temperament"; name: string }) => {
      if (!clinicId) throw new Error("clinic_required");
      const trimmed = input.name.trim();
      if (!trimmed) throw new Error("name_required");
      const { data, error } = await supabase
        .from("conditions")
        .insert({
          name: trimmed,
          type: input.type,
          clinic_id: clinicId,
        } as never)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["conditions"] });
    },
  });
}

export function useUpdateClinicCondition() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      if (!clinicId) throw new Error("clinic_required");
      const trimmed = input.name.trim();
      if (!trimmed) throw new Error("name_required");
      const { data, error } = await supabase
        .from("conditions")
        .update({ name: trimmed } as never)
        .eq("id", input.id)
        .eq("clinic_id", clinicId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["conditions"] });
    },
  });
}

export function useDeleteClinicCondition() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: { id: string }) => {
      if (!clinicId) throw new Error("clinic_required");
      const { error } = await supabase
        .from("conditions")
        .delete()
        .eq("id", input.id)
        .eq("clinic_id", clinicId);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["conditions"] });
    },
  });
}
